import { useQuery } from "@tanstack/react-query";

import {
  ITriggeredWorkflow,
  IWorkflowRun,
  IWorkflowStepState,
} from "../../../@types/admin/AdminWorkflow";
import { workflowsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/** Patients with a triggered workflow — backs the shared `WorkflowsPanel`. */
export function useTriggeredWorkflows(patientId?: number) {
  const { data, isLoading, isError } = useQuery<ITriggeredWorkflow[]>({
    queryKey: ["admin-triggered-workflows", patientId ?? "all"],
    queryFn: async () =>
      ensureOk(await workflowsService.getTriggeredWorkflows(patientId)),
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return { triggeredWorkflows: data ?? [], isLoading, isError };
}

/**
 * One patient's runs. Idle until a patient is chosen, matching Retool's
 * modal, which only ran its query once opened.
 */
export function usePatientWorkflowHistory(patientId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IWorkflowRun[]>({
    queryKey: ["admin-workflow-history", patientId],
    queryFn: async () =>
      ensureOk(await workflowsService.getPatientWorkflowHistory(patientId!)),
    enabled: patientId !== undefined,
    refetchOnMount: "always",
  });

  return { runs: data ?? [], isLoading, isError };
}

/** The executed steps of one run. Idle until a run is chosen. */
export function useWorkflowRunSteps(
  workflowPatientStateId: string | undefined
) {
  const { data, isLoading, isError } = useQuery<IWorkflowStepState[]>({
    queryKey: ["admin-workflow-run-steps", workflowPatientStateId],
    queryFn: async () =>
      ensureOk(await workflowsService.getRunSteps(workflowPatientStateId!)),
    enabled: Boolean(workflowPatientStateId),
    refetchOnMount: "always",
  });

  return { steps: data ?? [], isLoading, isError };
}
